import type { QueryClient } from '@tanstack/react-query'
import type { ApiClient } from '~/api/apiClient'
import type { PinV1 } from '~/pin/PinV1'
import {
  walkTimeline,
  walkAbsolute,
  walkRatio,
  walkRatioAbsolute,
  walkWrapper,
  type WalkTimelineParams,
  type WalkAbsoluteParams,
  type WalkRatioParams,
  type WalkRatioAbsoluteParams,
  type WalkWrapperParams,
  type WalkTimelineRow,
  type WalkAbsoluteRow,
  type WalkWrapperRow,
} from '~/api/endpoints/walks'
import { clampFromToFloor, getModelFloorIso } from '~/state/modelFloor'
import { getDimCatalog } from '~/api/endpoints/catalog'
import { driverDimsOf, resolveDriverArmFilters } from '~/pin/driverArmFilters'
import type { FlatInputRow } from './types'
import type { WalkSlice } from './flattenMultiWalk'

/**
 * Imperative per-pin walk runner for the data-export layer. Re-issues exactly the walk(s) a
 * pin's ChartFromPin arm renders (single-walk families + the three multi-walk families), so the
 * exported rows equal what the chart shows. Task 6 (pinToSheet) dispatches on `PinData.kind`.
 *
 * SOURCE OF TRUTH: frontend/src/components/Charts/ChartFromPin.tsx. Where this file diverges from
 * the plan skeleton, it was reconciled TO ChartFromPin (see the three comments tagged RECONCILED).
 */
export type PinData = {
  kind: 'flat' | 'wrapper' | 'multi'
  hasTime: boolean
  factTable: string
  single?: FlatInputRow[]
  wrapper?: WalkWrapperRow[]
  slices?: WalkSlice[]
  /** FU-03: timeframe labels whose driver slice was campaign-blocked (the exact Campaign dim
   *  cannot apply to the driver fact — the walk was never fired; pinToSheet renders an
   *  omission row per label). */
  driverBlocked?: string[]
}

type Deps = { client: ApiClient; queryClient: QueryClient }
/** Campaigns C1 Task 9: `filter_dims` mirrors the frozen per-arm predicate ChartFromPin's `Tf`
 *  shape gained in Task 8 (a campaign arm's merged+fact-pruned filters). Absent = the pin's base
 *  filter_dims applies (today's single-timeframe behavior, unchanged). */
export type Tf = {
  id: string
  label?: string
  from: string
  to: string
  filter_dims?: Record<string, string[]>
}
type Agg = 'sum' | 'avg' | 'max' | 'min'

function clamp(from: string): string {
  return clampFromToFloor(from, getModelFloorIso())
}

/** Replay value_type filter (mirrors the single-walk arms in ChartFromPin: keep rows with no
 *  value_type, or whose value_type is in the pin's saved set). No-op when the pin saved none. */
function applyVtFilter(rows: FlatInputRow[], vts?: string[]): FlatInputRow[] {
  if (!vts || vts.length === 0) return rows
  return rows.filter((r) => r.value_type == null || vts.includes(r.value_type))
}

// --- per-fn walk runners -----------------------------------------------------
// Each builds the canonical param object the matching useWalk* hook builds, and fetches under the
// same walk-fn key prefix (['walk_timeline', p] …) so an on-screen pin is a best-effort cache hit
// (a miss simply re-fetches — cache reuse is best-effort, not correctness). `from` is floor-clamped
// for parity with the live queries (walks.ts clamps again server-side; clamping here keeps the key
// aligned with the hooks' useFloorClampedParams).

function runTimeline(
  pin: PinV1,
  tf: Tf,
  d: Deps,
  aggregation: Agg,
  filterDims: Record<string, string[]>,
): Promise<WalkTimelineRow[]> {
  const p: WalkTimelineParams = {
    fact_table: pin.walk_call.fact_table,
    kpi: pin.walk_call.kpi,
    from: clamp(tf.from),
    to: tf.to,
    filter_dims: filterDims,
    group_by: pin.walk_call.params.group_by,
    bucket_size: pin.walk_call.params.bucket_size ?? 'month',
    aggregation,
  }
  return d.queryClient.fetchQuery({
    queryKey: ['walk_timeline', p] as const,
    queryFn: () => walkTimeline(d.client, p),
  })
}

function runAbsolute(
  pin: PinV1,
  tf: Tf,
  d: Deps,
  aggregation: Agg,
  filterDims: Record<string, string[]>,
): Promise<WalkAbsoluteRow[]> {
  const p: WalkAbsoluteParams = {
    fact_table: pin.walk_call.fact_table,
    kpi: pin.walk_call.kpi,
    from: clamp(tf.from),
    to: tf.to,
    filter_dims: filterDims,
    group_by: pin.walk_call.params.group_by,
    aggregation,
  }
  return d.queryClient.fetchQuery({
    queryKey: ['walk_absolute', p] as const,
    queryFn: () => walkAbsolute(d.client, p),
  })
}

function runRatio(
  pin: PinV1,
  tf: Tf,
  d: Deps,
  filterDims: Record<string, string[]>,
): Promise<WalkTimelineRow[]> {
  const p: WalkRatioParams = {
    kpi: pin.walk_call.kpi,
    from: clamp(tf.from),
    to: tf.to,
    filter_dims: filterDims,
    group_by: pin.walk_call.params.group_by,
    bucket_size: pin.walk_call.params.bucket_size ?? 'month',
  }
  return d.queryClient.fetchQuery({
    queryKey: ['walk_ratio', p] as const,
    queryFn: () => walkRatio(d.client, p),
  })
}

function runRatioAbsolute(
  pin: PinV1,
  tf: Tf,
  d: Deps,
  filterDims: Record<string, string[]>,
): Promise<WalkAbsoluteRow[]> {
  const p: WalkRatioAbsoluteParams = {
    kpi: pin.walk_call.kpi,
    from: clamp(tf.from),
    to: tf.to,
    filter_dims: filterDims,
    group_by: pin.walk_call.params.group_by,
  }
  return d.queryClient.fetchQuery({
    queryKey: ['walk_ratio_absolute', p] as const,
    queryFn: () => walkRatioAbsolute(d.client, p),
  })
}

/** Dispatch the pin's BASE ("metric") walk on `walk_call.function`. Ratio families ignore
 *  `aggregation` (their RPCs take none — see walks.ts). Rows widen to FlatInputRow[] (timeline
 *  rows carry `time`; absolute rows omit it — fine, the caller sets `hasTime` accordingly). */
function runBase(
  pin: PinV1,
  tf: Tf,
  d: Deps,
  aggregation: Agg,
  filterDims: Record<string, string[]>,
): Promise<FlatInputRow[]> {
  switch (pin.walk_call.function) {
    case 'walk_timeline':
      return runTimeline(pin, tf, d, aggregation, filterDims)
    case 'walk_absolute':
      return runAbsolute(pin, tf, d, aggregation, filterDims)
    case 'walk_ratio':
      return runRatio(pin, tf, d, filterDims)
    case 'walk_ratio_absolute':
      return runRatioAbsolute(pin, tf, d, filterDims)
    default:
      throw new Error(`fetchPinData: unsupported base walk function "${pin.walk_call.function}"`)
  }
}

type DriverVp = { driver_kpi?: string; driver_fact?: string }

/** Driver ("driver") arm walk, per family (ChartFromPin):
 *  - metric_driver_bars → useTimeframeFanout family:'absolute' → walkAbsolute (L426-443).
 *  - dynamics_panels    → useDynamicsPanels family:'timeline'  → walkTimeline (L545-559).
 *  Both use the driver kpi/fact + the caller-resolved `filterDims` + the pin's own group_by,
 *  aggregation 'sum'.
 *
 *  FU-03 (supersedes the Task 9 RECONCILED no-strip mandate): `filterDims` arrives here already
 *  STRIPPED to the driver fact's dims by the caller — the same pin/driverArmFilters rule
 *  ChartFromPin's driver arms apply — so chart and export still cannot diverge (the parity now
 *  lives in the shared helper instead of in shared non-stripping). */
function runDriver(
  pin: PinV1,
  tf: Tf,
  vp: DriverVp,
  d: Deps,
  filterDims: Record<string, string[]>,
): Promise<FlatInputRow[]> {
  const driverKpi = vp.driver_kpi!
  if (pin.render_hint.chart_type === 'metric_driver_bars') {
    // MetricDriverBarsFromPin uses `vp.driver_fact!` (driver_kpi/fact are saved as a pair).
    const p: WalkAbsoluteParams = {
      fact_table: vp.driver_fact!,
      kpi: driverKpi,
      from: clamp(tf.from),
      to: tf.to,
      filter_dims: filterDims,
      group_by: pin.walk_call.params.group_by,
      aggregation: 'sum',
    }
    return d.queryClient.fetchQuery({
      queryKey: ['walk_absolute', p] as const,
      queryFn: () => walkAbsolute(d.client, p),
    })
  }
  // dynamics_panels driver — always the timeline family (even when the metric is a ratio);
  // DynamicsPanelsFromPin falls back to the pin's own fact when driver_fact is absent.
  const p: WalkTimelineParams = {
    fact_table: vp.driver_fact ?? pin.walk_call.fact_table,
    kpi: driverKpi,
    from: clamp(tf.from),
    to: tf.to,
    filter_dims: filterDims,
    group_by: pin.walk_call.params.group_by,
    bucket_size: pin.walk_call.params.bucket_size ?? 'month',
    aggregation: 'sum',
  }
  return d.queryClient.fetchQuery({
    queryKey: ['walk_timeline', p] as const,
    queryFn: () => walkTimeline(d.client, p),
  })
}

/** Frozen per-pin timeframes (default: one 'main' window from params.from/to), matching the
 *  `vp.timeframes ?? [{ id:'main', label:'', ... }]` fallback in every multi-walk arm. Exported
 *  for pinToSheet.ts (Task 9): the sheet-metadata rows reuse this as the single source of truth
 *  for which entries carry a frozen per-arm filter_dims, instead of re-deriving it. */
export function timeframesOf(pin: PinV1): Tf[] {
  const vp = (pin.render_hint.viz_params ?? {}) as { timeframes?: Tf[] }
  return vp.timeframes && vp.timeframes.length > 0
    ? vp.timeframes
    : [{ id: 'main', label: '', from: pin.walk_call.params.from, to: pin.walk_call.params.to }]
}

// Full `${function}:${chart_type}` dispatch keys ChartFromPin routes to a multi-walk arm.
export const MULTI_KEYS = new Set([
  'walk_ratio_absolute:metric_driver_bars',
  'walk_absolute:metric_driver_bars',
  'walk_ratio:dynamics_panels',
  'walk_timeline:dynamics_panels',
  'walk_timeline:multi_timeframe_overlay',
  'walk_ratio:multi_timeframe_overlay',
])

export async function fetchPinData(pin: PinV1, d: Deps): Promise<PinData> {
  const fn = pin.walk_call.function
  const chart = pin.render_hint.chart_type
  const vts = pin.walk_call.params.value_types
  const factTable = pin.walk_call.fact_table
  const key = `${fn}:${chart}`

  // --- wrapper (walk_wrapper:wrapper) ---
  if (key === 'walk_wrapper:wrapper') {
    const p: WalkWrapperParams = {
      fact_table: factTable,
      kpi: pin.walk_call.kpi,
      from: clamp(pin.walk_call.params.from),
      to: pin.walk_call.params.to,
      filter_dims: pin.walk_call.params.filter_dims,
      group_by: pin.walk_call.params.group_by,
    }
    const rows = await d.queryClient.fetchQuery({
      queryKey: ['walk_wrapper', p] as const,
      queryFn: () => walkWrapper(d.client, p),
    })
    return { kind: 'wrapper', hasTime: true, factTable, wrapper: rows }
  }

  // --- multi-walk families (metric/driver fan-out over frozen timeframes) ---
  if (MULTI_KEYS.has(key)) {
    const tfs = timeframesOf(pin)
    // metric_driver_bars renders ABSOLUTE bars (no time axis); the other two overlay timelines.
    const hasTime = chart !== 'metric_driver_bars'
    const vp = (pin.render_hint.viz_params ?? {}) as DriverVp
    // FU-03: resolve the driver fact's dim set ONCE per pin (same query key as useDimCatalog —
    // a cache hit when the UI already loaded it). null = catalog unavailable → driver slices
    // fire unstripped (pre-FU-03 behavior) with a loud warn, mirroring ChartFromPin's degrade.
    let driverDims: Set<string> | null = null
    if (vp.driver_kpi) {
      const driverFact =
        chart === 'metric_driver_bars' ? vp.driver_fact! : (vp.driver_fact ?? factTable)
      try {
        const catalog = await d.queryClient.fetchQuery({
          queryKey: ['v_dim_catalog'] as const,
          queryFn: () => getDimCatalog(d.client),
        })
        driverDims = driverDimsOf(catalog, driverFact)
      } catch (err) {
        console.warn(
          'fetchPinData: dim catalog unavailable — driver slices export unstripped filters.',
          err,
        )
      }
    }
    const driverBlocked: string[] = []
    const slices: WalkSlice[] = []
    for (const tf of tfs) {
      const label = tf.label || tf.id
      // Campaigns C1 Task 9 (binding parity mandate): a frozen per-arm predicate (Task 8) overrides
      // the pin's base filter_dims for THIS tf's walk(s) — mirrors ChartFromPin's
      // `tf.filter_dims ?? pin.walk_call.params.filter_dims` exactly, for BOTH arms below.
      const filterDims = tf.filter_dims ?? pin.walk_call.params.filter_dims
      const metric = await runBase(pin, tf, d, 'sum', filterDims)
      // RECONCILED (vs. skeleton): NO value_type filter on multi-walk slices — ChartFromPin's
      // MetricDriverBarsFromPin / DynamicsPanelsFromPin / MultiTimeframeOverlayFromPin never
      // replay params.value_types (only the single-walk arms do). Export unfiltered to match.
      slices.push({ timeframe: label, arm: vp.driver_kpi ? 'metric' : undefined, rows: metric })
      if (vp.driver_kpi) {
        // FU-03: the driver slice fires the fact-valid subset of this tf's filters. A
        // campaign-exact filter the driver fact can't carry BLOCKS the slice (a stripped walk
        // would silently widen to unfiltered totals) — recorded on driverBlocked for the sheet's
        // omission row, warned like the existing driver-failure path below.
        const resolved = driverDims ? resolveDriverArmFilters(filterDims, driverDims) : null
        if (resolved?.campaignBlocked) {
          console.warn(
            `fetchPinData: campaign filter not applicable to the driver fact for pin ` +
              `"${pin.render_hint.title ?? pin.walk_call.kpi}" (timeframe ${label}); ` +
              `driver slice omitted.`,
          )
          driverBlocked.push(label)
        } else {
          // Driver arm degrades gracefully in ChartFromPin (the source of truth): a
          // DimNotApplicableError (driver fact can't carry the pin's group_by) or any driver-walk
          // failure renders a note/empty driver while the metric arm still shows. So the export
          // keeps the metric slice and drops only the failed driver slice, never aborting the
          // whole pin. Not a silent fallback: the failure is warned, and the metric data the
          // chart shows is still exported.
          try {
            const driver = await runDriver(pin, tf, vp, d, resolved?.filters ?? filterDims)
            slices.push({
              timeframe: label,
              arm: 'driver',
              rows: driver,
              ...(resolved && resolved.dropped.length > 0
                ? { effective: resolved.filters, dropped: resolved.dropped }
                : {}),
            })
          } catch (err) {
            console.warn(
              `fetchPinData: driver arm failed for pin "${pin.render_hint.title ?? pin.walk_call.kpi}"` +
                ` (timeframe ${label}); exporting the metric arm only.`,
              err,
            )
          }
        }
      }
    }
    return {
      kind: 'multi',
      hasTime,
      factTable,
      slices,
      ...(driverBlocked.length > 0 ? { driverBlocked } : {}),
    }
  }

  // --- single-walk flat families (timeline / absolute / ratio / ratio_absolute / summary strip) ---
  const hasTime = fn === 'walk_timeline' || fn === 'walk_ratio'
  const rows = await runBase(
    pin,
    { id: 'main', from: pin.walk_call.params.from, to: pin.walk_call.params.to },
    d,
    pin.walk_call.params.aggregation ?? 'sum',
    pin.walk_call.params.filter_dims,
  )
  // RECONCILED (vs. skeleton): summary_bar_strip is a grand-total-per-period overview and, unlike
  // the chart arms, has NO value_type replay filter (ChartFromPin SummaryStripFromPin applies no
  // vt filter; since FY v2 it folds rows to the pin's frozen display unit when that differs from
  // the wire bucket — grain parity lives in pinToSheet's foldPinData, which folds this export the
  // same way). Only the four value-type-aware arms filter.
  const single = chart === 'summary_bar_strip' ? rows : applyVtFilter(rows, vts)
  return { kind: 'flat', hasTime, factTable, single }
}
